import { isApiError, isNetworkError, unwrap, type PinRoster } from '@qrkey/api-client';
import { Alert, Button, Card, PinPad, Spinner } from '@qrkey/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { api } from '../app/api.js';
import { terminalCredential, useTerminal, type DeviceTerminal } from '../app/terminal.js';
import { errorMessage } from '../lib/errors.js';
import { connectivity, useConnectivity } from '../offline/connectivity.js';
import { lockedUntil, offlineRoster, verifyOfflinePin } from '../offline/pin.js';
import { OfflineRefusal } from '../offline/refusal.js';
import { startOfflineSession } from '../offline/session.js';
import { AuthLayout } from './AuthLayout.js';
import { useFinishSignIn } from './SignInScreen.js';

interface Person {
  userId: string;
  name: string;
  roleName: string;
  pinLockedUntil: string | null;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('');
}

/** The till's PIN screen: pick yourself from the roster, enter your PIN. */
export function PinScreen() {
  const terminal = useTerminal();
  if (!terminal) {
    return (
      <AuthLayout
        title="This device is not a till yet"
        description="A manager sets it up once; after that staff sign in with their PIN."
      >
        <div className="stack">
          <Link
            to="/login"
            search={{ redirect: '/setup/terminal' }}
            className="qk-btn qk-btn--primary qk-btn--lg qk-btn--block"
          >
            <span className="qk-btn__label">Set up this device</span>
          </Link>
          <Link to="/login">Sign in with a password</Link>
        </div>
      </AuthLayout>
    );
  }
  return <PinSignIn terminal={terminal} />;
}

function PinSignIn({ terminal }: { terminal: DeviceTerminal }) {
  const finish = useFinishSignIn();
  const [person, setPerson] = useState<Person | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const online = useConnectivity();
  const roster = useQuery<PinRoster>({
    queryKey: ['pin-roster', terminal.terminalId, online],
    queryFn: async () => {
      if (connectivity.isOnline()) {
        try {
          const r = await unwrap(
            api.client.POST('/v1/auth/pin/roster', {
              body: { terminal: terminalCredential(terminal) },
            }),
          );
          connectivity.reportAnswer();
          return r;
        } catch (e) {
          if (!isNetworkError(e)) throw e;
          connectivity.reportFailure();
        }
      }
      // No cloud: the people this till can sign in on its own (stored while it was online).
      const people = await offlineRoster(terminal.outletId);
      if (people.length === 0) {
        throw new OfflineRefusal(
          'NO_OFFLINE_PINS',
          'There is no connection and this till has not stored staff sign-ins yet. Connect it once and sign in.',
        );
      }
      return {
        staff: await Promise.all(
          people.map(async (p) => {
            const until = await lockedUntil(p.userId);
            return {
              userId: p.userId,
              name: p.name,
              roleName: p.roleName,
              pinLockedUntil: until ? new Date(until).toISOString() : null,
            };
          }),
        ),
      } as PinRoster;
    },
    staleTime: 30_000,
    retry: (count, e) => !isApiError(e) && !(e instanceof OfflineRefusal) && count < 2,
  });

  /** Sign in on this till without the cloud: the stored verifier, then a local session. */
  const signInOffline = useCallback(
    async (who: Person, value: string) => {
      const r = await verifyOfflinePin(terminal.outletId, who.userId, value);
      if (r.ok) {
        finish(startOfflineSession(r.person, value));
        return;
      }
      setPin('');
      setError(
        r.reason === 'wrong'
          ? 'Wrong PIN. Try again.'
          : r.reason === 'locked'
            ? `Too many wrong tries. Try again in ${Math.max(1, Math.ceil(((r.lockedUntil ?? Date.now()) - Date.now()) / 60_000))} minutes.`
            : 'This person cannot sign in without the connection.',
      );
    },
    [finish, terminal.outletId],
  );

  const submit = useCallback(
    async (value: string) => {
      if (!person || busy) return;
      setBusy(true);
      setError(undefined);
      try {
        if (!connectivity.isOnline()) {
          await signInOffline(person, value);
          return;
        }
        const pair = await unwrap(
          api.client.POST('/v1/auth/pin', {
            body: { terminal: terminalCredential(terminal), userId: person.userId, pin: value },
          }),
        );
        finish(pair);
      } catch (e) {
        if (isNetworkError(e)) {
          // The connection went while signing in: the stored verifier decides.
          connectivity.reportFailure();
          await signInOffline(person, value);
          return;
        }
        setPin('');
        setError(errorMessage(e, 'pin'));
        if (isApiError(e, 'ACCOUNT_LOCKED')) void roster.refetch();
      } finally {
        setBusy(false);
      }
    },
    [busy, finish, person, roster, signInOffline, terminal],
  );

  if (person) {
    const locked = person.pinLockedUntil !== null && Date.parse(person.pinLockedUntil) > Date.now();
    return (
      <AuthLayout
        title={`Hi, ${person.name.split(' ')[0] ?? person.name}`}
        description={person.roleName}
      >
        <div className="stack">
          {locked ? (
            <Alert tone="warning">PIN sign-in is locked for a while after wrong tries.</Alert>
          ) : null}
          <PinPad
            value={pin}
            onChange={setPin}
            onSubmit={(v) => void submit(v)}
            disabled={busy}
            error={error}
          />
          <Button
            variant="ghost"
            size="lg"
            block
            onClick={() => {
              setPerson(null);
              setPin('');
              setError(undefined);
            }}
          >
            Not {person.name}? Choose someone else
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Who is signing in?" description={`${terminal.name} · tap your name`} wide>
      <div className="stack">
        {roster.isPending ? <Spinner label="Loading staff" size="lg" /> : null}
        {roster.isError ? (
          <Alert
            tone="danger"
            action={
              <Button variant="secondary" onClick={() => void roster.refetch()}>
                Try again
              </Button>
            }
          >
            {errorMessage(roster.error)}
          </Alert>
        ) : null}
        {roster.data && roster.data.staff.length === 0 ? (
          <Alert tone="info">
            Nobody at this outlet has a PIN yet. A manager sets PINs in Staff.
          </Alert>
        ) : null}
        {roster.data ? (
          <ul className="roster" aria-label="Staff">
            {roster.data.staff.map((p) => (
              <li key={p.userId}>
                <button type="button" className="roster__person" onClick={() => setPerson(p)}>
                  <span className="roster__avatar" aria-hidden="true">
                    {initials(p.name)}
                  </span>
                  <span className="roster__name">{p.name}</span>
                  <span className="roster__role">{p.roleName}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <div className="auth__links">
          <Link to="/login">Sign in with a password</Link>
        </div>
      </div>
    </AuthLayout>
  );
}

export function NotFoundScreen() {
  return (
    <main id="main" className="auth__main">
      <Card title="Page not found" titleLevel={1}>
        <Link to="/">Go to the start</Link>
      </Card>
    </main>
  );
}
